import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Drawer,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Alert,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useGetApplicationsByProjectIdQuery,
  useGetConfigDetailsQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
  useGetListOfTagsQuery,
  useCreateTagMutation,
} from "@/features/bughurd/api";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import {
  useGetProjectOrReposForImportQuery,
  useGetRepoBranchesMutation,
} from "@/features/CompanyInformation/companyApi";

// ── Types ──
interface Repo {
  url: string;
  branch: string;
  description: string;
  currentRelease: string;
  incomingRelease: string;
}

interface Application {
  _id?: string;
  name: string;
  description: string;
  type: "custom" | "saas" | "hybrid";
  repos: Repo[];
  platform?: string;
  platformVersion?: string;
  envUrl?: string;
  orgId?: string;
  createdBy?: any;
  createdAt?: string;
}

// ── Constants ──
const applicationTypeOptions = [
  { key: "custom", label: "Custom", icon: "⌨", desc: "Code repositories only" },
  { key: "saas", label: "SaaS", icon: "☁", desc: "Cloud platform binding" },
  { key: "hybrid", label: "Hybrid", icon: "⚙", desc: "Repos + SaaS platform" },
];

const emptyRepo = (): Repo => ({
  url: "",
  branch: "main",
  description: "",
  currentRelease: "",
  incomingRelease: "",
});

const emptyApp = (): Application => ({
  name: "",
  description: "",
  type: "custom",
  repos: [emptyRepo()],
  platform: "",
  platformVersion: "",
  envUrl: "",
  orgId: "",
});

// ── Helper: map API response item to local Application shape ──
const mapApiApplication = (item: any): Application => ({
  _id: item._id,
  name: item.name || "",
  description: item.description || "",
  type: (item.applicationType?.toLowerCase() ||
    "custom") as Application["type"],
  repos: (item.repositories || []).map((r: any) => ({
    url: r.repositoryUrl || "",
    branch: r.defaultBranch || "main",
    description: r.description || "",
    currentRelease: r.currentReleaseTag || "",
    incomingRelease: r.incomingReleaseTag || "",
  })),
  platform: item.saasPlatformBinding?.platform || "",
  platformVersion: item.saasPlatformBinding?.currentRelease || "",
  envUrl: item.saasPlatformBinding?.environmentUrl || "",
  orgId: item.saasPlatformBinding?.orgInstanceId || "",
  createdBy: item.createdBy,
  createdAt: item.createdAt,
});

// ── Helpers ──
const typeColors: Record<string, { bg: string; color: string; label: string }> =
  {
    custom: { bg: "#dbeafe", color: "#2563eb", label: "Custom" },
    saas: { bg: "#d1fae5", color: "#059669", label: "SaaS" },
    hybrid: { bg: "#ede9fe", color: "#7c3aed", label: "Hybrid" },
  };

export default function ApplicationList() {
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  // Fallback: extract project ID from URL if useParams doesn't resolve
  const id =
    paramId || window.location.pathname.split("/projects/")[1]?.split("/")[0];

  const [tabValue, setTabValue] = useState("apps");
  const [search, setSearch] = useState("");

  // Drawer state for Add/Edit Application
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<Application>(emptyApp());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedRepos, setExpandedRepos] = useState(true);
  const [platformInput, setPlatformInput] = useState("");
  const [isCreatingPlatform, setIsCreatingPlatform] = useState(false);

  // Tags API for platform options (filtered by type=APPLICATION)
  const { data: tagList, refetch: refetchTags } = useGetListOfTagsQuery(
    { projectId: id || "", type: "APPLICATION" },
    { skip: !id },
  );
  const DEFAULT_PLATFORMS = ["Salesforce", "ServiceNow", "Workday", "SAP"];
  const availablePlatforms = tagList?.data?.decryptedData?.items || [];
  const apiPlatforms: string[] = availablePlatforms.map((t: any) => t.tagName);
  const platformOptions: string[] = Array.from(
    new Set([...DEFAULT_PLATFORMS, ...apiPlatforms]),
  );
  const [createTag] = useCreateTagMutation();

  console.log("Current project state:", id);
  // Fetch applications by project ID
  const {
    data: applicationsData,
    isLoading: applicationsLoading,
    error: applicationsError,
    isFetching: applicationsFetching,
    refetch,
  } = useGetApplicationsByProjectIdQuery(
    { projectId: id || "" },
    {
      skip: !id,
      refetchOnMountOrArgChange: true,
    },
  );
  // Map API data to local shape
  const applications: Application[] = (
    applicationsData?.data?.decryptedData?.items || []
  ).map(mapApiApplication);
  const totalApplications =
    applicationsData?.data?.decryptedData?.totalCount || applications.length;

  const [createApplication, { isLoading: isCreating }] =
    useCreateApplicationMutation();
  const [updateApplication, { isLoading: isUpdating }] =
    useUpdateApplicationMutation();
  const [deleteApplication, { isLoading: isDeleting }] =
    useDeleteApplicationMutation();

  // Git config & repo/branch APIs
  const { data: configData } = useGetConfigDetailsQuery({});
  const isGitConfigured = configData?.data?.decryptedData?.some(
    (config: any) => config.type === "GIT_HUB",
  );
  const { data: repoData, isLoading: isRepoLoading } =
    useGetProjectOrReposForImportQuery("GIT_HUB", {
      skip: !isGitConfigured,
    });
  console.log("Git config data:", repoData);
  const [getRepoBranches] = useGetRepoBranchesMutation();
  const [branchDataMap, setBranchDataMap] = useState<
    Record<string, { data: any[]; loading: boolean }>
  >({});

  const fetchBranches = async (repoIndex: number, repoName: string) => {
    const key = `${repoIndex}`;
    setBranchDataMap((prev) => ({
      ...prev,
      [key]: { data: prev[key]?.data || [], loading: true },
    }));
    try {
      const res: any = await getRepoBranches({
        repoName,
        from: "GIT_HUB",
      }).unwrap();
      setBranchDataMap((prev) => ({
        ...prev,
        [key]: {
          data: res?.data?.decryptedData?.data || [],
          loading: false,
        },
      }));
    } catch {
      setBranchDataMap((prev) => ({
        ...prev,
        [key]: { data: [], loading: false },
      }));
    }
  };

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    index: number;
    appId: string;
    name: string;
    repoCount: number;
  }>({ open: false, index: -1, appId: "", name: "", repoCount: 0 });

  // ── Drawer Handlers ──
  const handleOpenAddDrawer = () => {
    setEditingApp(emptyApp());
    setEditingIndex(null);
    setExpandedRepos(true);
    setDrawerOpen(true);
  };

  const handleOpenEditDrawer = (index: number) => {
    setEditingApp(JSON.parse(JSON.stringify(applications[index])));
    setEditingIndex(index);
    setExpandedRepos(true);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingApp(emptyApp());
    setEditingIndex(null);
  };

  const handleSaveApplication = async () => {
    if (!editingApp.name.trim() || !id) return;

    const isEditing = editingIndex !== null && editingApp._id;

    const payload: any = {
      name: editingApp.name,
      applicationType: editingApp.type?.toUpperCase(),
      description: editingApp.description || "",
      repositories: editingApp.repos
        ?.filter((r) => r.url)
        .map((r) => ({
          repositoryUrl: r.url,
          defaultBranch: r.branch || "main",
          description: r.description || "",
          currentReleaseTag: r.currentRelease || "",
          incomingReleaseTag: r.incomingRelease || "",
        })),
    };

    if (editingApp.type === "saas" || editingApp.type === "hybrid") {
      payload.saasPlatformBinding = {
        platform: editingApp.platform || "",
        currentRelease: editingApp.platformVersion || "",
        environmentUrl: editingApp.envUrl || "",
        orgInstanceId: editingApp.orgId || "",
      };
    }

    try {
      if (isEditing) {
        await updateApplication({
          applicationId: editingApp._id,
          ...payload,
        }).unwrap();
        toast.success(t("Application updated successfully"));
      } else {
        await createApplication({
          projectId: id,
          ...payload,
        }).unwrap();
        toast.success(t("Application created successfully"));
      }
      handleCloseDrawer();
    } catch (err) {
      console.error("Failed to save application:", err);
      toast.error(
        isEditing
          ? t("Failed to update application")
          : t("Failed to create application"),
      );
    }
  };

  // ── Repo Handlers (inside drawer) ──
  const addRepo = () => {
    setEditingApp((prev) => ({
      ...prev,
      repos: [...prev.repos, emptyRepo()],
    }));
  };

  const updateRepo = (repoIndex: number, updated: Repo) => {
    setEditingApp((prev) => {
      const repos = [...prev.repos];
      repos[repoIndex] = updated;
      return { ...prev, repos };
    });
  };

  const removeRepo = (repoIndex: number) => {
    setEditingApp((prev) => {
      const repos = [...prev.repos];
      repos.splice(repoIndex, 1);
      return { ...prev, repos };
    });
  };

  // ── Delete Handlers ──
  const handleOpenDeleteDialog = (index: number) => {
    const app = applications[index];
    setDeleteDialog({
      open: true,
      index,
      appId: app._id || "",
      name: app.name,
      repoCount: app.repos?.length || 0,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.appId) return;
    try {
      await deleteApplication(deleteDialog.appId).unwrap();
      toast.success(t("Application deleted successfully"));
      // Refetch the applications list after successful deletion
      await refetch();
    } catch (err) {
      console.error("Failed to delete application:", err);
      toast.error(t("Failed to delete application"));
    }
    setDeleteDialog({
      open: false,
      index: -1,
      appId: "",
      name: "",
      repoCount: 0,
    });
  };

  // ── Computed ──
  const totalRepos = applications.reduce(
    (sum, app) => sum + (app.repos?.length || 0),
    0,
  );

  const allRepos = applications.flatMap((app) =>
    (app.repos || []).map((r) => ({
      ...r,
      appName: app.name,
      appType: app.type,
    })),
  );

  const filteredApps = applications.filter((app) =>
    app.name.toLowerCase().includes(search.toLowerCase()),
  );

  const filteredRepos = allRepos.filter(
    (repo) =>
      repo.url.toLowerCase().includes(search.toLowerCase()) ||
      repo.appName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        overflow: "auto",
        mx: "auto",
      }}
    >
      {/* Breadcrumb */}
      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
        <Typography
          variant="caption"
          onClick={() => navigate("/defect-tracker/projects")}
          sx={{
            fontSize: "11.5px",
            color: "text.secondary",
            cursor: "pointer",
            "&:hover": { color: "primary.main", textDecoration: "underline" },
          }}
        >
          {t("Projects")}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: "11.5px", color: "text.disabled" }}
        >
          /
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: "11.5px", color: "text.primary", fontWeight: 600 }}
        >
          {t("Applications")}
        </Typography>
      </Box>

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2.5}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} fontSize="22px">
            {t("Applications")}
          </Typography>

          {/* Stats */}
          <Box display="flex" alignItems="stretch" gap={1.5} mt={2}>
            {applications[0]?.createdBy?.fullName && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "12px",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                    }`,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(0,0,0,0.015)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {applications[0].createdBy.fullName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {t("Owner")}
                </Typography>
              </Box>
            )}
            {[
              {
                value: totalApplications,
                label: t("Applications"),
                color: (theme: any) => theme.palette.primary.main,
                accent: (theme: any) =>
                  theme.palette.mode === "dark"
                    ? `${theme.palette.primary.main}18`
                    : `${theme.palette.primary.main}0A`,
              },
              {
                value: totalRepos,
                label: t("Repositories"),
                color: () => "#3b82f6",
                accent: (theme: any) =>
                  theme.palette.mode === "dark"
                    ? "rgba(59,130,246,0.1)"
                    : "rgba(59,130,246,0.04)",
              },
            ].map((stat, i) => (
              <Box
                key={i}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "12px",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                    }`,
                  bgcolor: stat.accent,
                  minWidth: 100,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "22px",
                    fontWeight: 800,
                    color: stat.color,
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={handleOpenAddDrawer}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12.5px",
            borderRadius: "8px",
            boxShadow: "none",
            flexShrink: 0,
            "&:hover": { boxShadow: "none" },
          }}
        >
          {t("Application")}
        </Button>
      </Box>

      {/* Tabs */}
      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={(_, v) => setTabValue(v)}
            sx={{
              minHeight: 38,
              "& .MuiTab-root": {
                minHeight: 38,
                fontSize: "13px",
                textTransform: "none",
                fontWeight: 500,
                py: 0.5,
                "&.Mui-selected": { fontWeight: 700 },
              },
            }}
          >
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={0.8}>
                  {t("Applications")}
                  <Chip
                    label={applications.length}
                    size="small"
                    color={tabValue === "apps" ? "primary" : "default"}
                    sx={{ fontSize: "10px", height: 18, fontWeight: 700 }}
                  />
                </Box>
              }
              value="apps"
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={0.8}>
                  {t("Repositories")}
                  <Chip
                    label={totalRepos}
                    size="small"
                    color={tabValue === "repos" ? "primary" : "default"}
                    sx={{ fontSize: "10px", height: 18, fontWeight: 700 }}
                  />
                </Box>
              }
              value="repos"
            />
          </TabList>
        </Box>

        {/* ── Applications Tab ── */}
        <TabPanel value="apps" sx={{ px: 0, pt: 2 }}>
          {applicationsLoading || applicationsFetching ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize="12px"
                  >
                    {applications.length} {t("applications")} &middot;{" "}
                    {totalRepos} {t("total repos")}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder={t("Search applications...")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ fontSize: 18, color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "12px",
                        borderRadius: "8px",
                        height: 32,
                      },
                      width: 220,
                    }}
                  />
                </Box>
              </Box>

              {filteredApps.length === 0 ? (
                <Paper
                  elevation={0}
                  onClick={!search ? handleOpenAddDrawer : undefined}
                  sx={{
                    p: 5,
                    textAlign: "center",
                    borderRadius: "12px",
                    border: (theme) => `2px dashed ${theme.palette.divider}`,
                    cursor: !search ? "pointer" : "default",
                    transition: "all 0.2s",
                    ...(!search && {
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: (theme: any) =>
                          `${theme.palette.primary.main}05`,
                      },
                    }),
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="primary"
                    fontSize="14px"
                  >
                    {t("No applications found")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="12px"
                  >
                    {search
                      ? t("Try adjusting your search")
                      : t("Add your first application to get started")}
                  </Typography>
                </Paper>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: "10px",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 1px 6px rgba(0,0,0,0.2)"
                        : "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        {[
                          "APPLICATION",
                          "TYPE",
                          "REPOS",
                          "DESCRIPTION",
                          "RELEASE",
                          "ACTIONS",
                        ].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontSize: "10px",
                              fontWeight: 700,
                              letterSpacing: 1,
                              color: "text.secondary",
                              borderBottomWidth: 2,
                              py: 1.2,
                            }}
                          >
                            {t(header)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredApps.map((app, index) => {
                        const typeInfo =
                          typeColors[app.type] || typeColors.custom;
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              transition: "background 0.15s",
                              "&:hover": {
                                bgcolor: (theme) =>
                                  `${theme.palette.primary.main}08`,
                              },
                            }}
                          >
                            {/* Application Name */}
                            <TableCell sx={{ py: 1.8 }}>
                              <Box display="flex" alignItems="center" gap={1.2}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "8px",
                                    bgcolor: "primary.main",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    flexShrink: 0,
                                  }}
                                >
                                  A{index + 1}
                                </Box>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  fontSize="13px"
                                >
                                  {app.name}
                                </Typography>
                              </Box>
                            </TableCell>

                            {/* Type */}
                            <TableCell sx={{ py: 1.8 }}>
                              <Chip
                                label={typeInfo.label}
                                size="small"
                                sx={{
                                  bgcolor: typeInfo.bg,
                                  color: typeInfo.color,
                                  fontWeight: 700,
                                  fontSize: "10.5px",
                                  height: 22,
                                  borderRadius: "5px",
                                }}
                              />
                            </TableCell>

                            {/* Repos Count */}
                            <TableCell sx={{ py: 1.8 }}>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                fontSize="13px"
                              >
                                {app.repos?.length || 0}
                              </Typography>
                            </TableCell>

                            {/* Description */}
                            <TableCell sx={{ py: 1.8, maxWidth: 280 }}>
                              <Typography
                                variant="body2"
                                fontSize="12px"
                                color="text.secondary"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {app.description}
                              </Typography>
                            </TableCell>

                            {/* Release */}
                            <TableCell sx={{ py: 1.8 }}>
                              {app.repos?.[0]?.currentRelease ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <Typography
                                    variant="caption"
                                    fontFamily="monospace"
                                    fontSize="11.5px"
                                    color="text.secondary"
                                  >
                                    {app.repos[0].currentRelease}
                                  </Typography>
                                  {app.repos[0].incomingRelease && (
                                    <>
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        fontSize="11px"
                                      >
                                        →
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        fontFamily="monospace"
                                        fontWeight={700}
                                        fontSize="11.5px"
                                        color="primary.main"
                                      >
                                        {app.repos[0].incomingRelease}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            {/* Actions */}
                            <TableCell sx={{ py: 1.8 }}>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title={t("Edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const realIndex =
                                        applications.indexOf(app);
                                      handleOpenEditDrawer(realIndex);
                                    }}
                                    sx={{
                                      color: "primary.main",
                                      fontSize: 14,
                                      "&:hover": {
                                        bgcolor: (theme) =>
                                          `${theme.palette.primary.main}12`,
                                      },
                                    }}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("Delete")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const realIndex =
                                        applications.indexOf(app);
                                      handleOpenDeleteDialog(realIndex);
                                    }}
                                    sx={{
                                      color: "error.main",
                                      fontSize: 14,
                                      "&:hover": {
                                        bgcolor: (theme) =>
                                          `${theme.palette.error.main}12`,
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </TabPanel>

        {/* ── Repositories Tab ── */}
        <TabPanel value="repos" sx={{ px: 0, pt: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize="12px"
            mb={2}
          >
            {allRepos.length}{" "}
            {allRepos.length === 1 ? t("repository") : t("repositories")}{" "}
            {t("across")} {applications.length}{" "}
            {applications.length === 1 ? t("application") : t("applications")}
          </Typography>

          {filteredRepos.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: "12px",
                border: (theme) => `2px dashed ${theme.palette.divider}`,
              }}
            >
              <CodeOutlinedIcon
                sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
              />
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                {t("No repositories yet")}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {t("Add repos by editing an application")}
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "10px",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 6px rgba(0,0,0,0.2)"
                    : "0 1px 4px rgba(0,0,0,0.04)",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "REPOSITORY",
                      "APPLICATION",
                      "BRANCH",
                      "DESCRIPTION",
                      "CURRENT",
                      "INCOMING",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: 1,
                          color: "text.secondary",
                          borderBottomWidth: 2,
                          py: 1.2,
                        }}
                      >
                        {t(header)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRepos.map((repo, index) => {
                    const typeInfo =
                      typeColors[repo.appType] || typeColors.custom;
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          transition: "background 0.15s",
                          "&:hover": {
                            bgcolor: (theme) =>
                              `${theme.palette.primary.main}06`,
                          },
                        }}
                      >
                        {/* Repository URL */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: "6px",
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "grey.800"
                                    : "grey.900",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <CodeOutlinedIcon
                                sx={{ fontSize: 13, color: "#fff" }}
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                fontSize="12px"
                                color="primary.main"
                              >
                                {repo.url.replace("github.com/", "")}
                              </Typography>
                              <Typography
                                variant="caption"
                                fontSize="10px"
                                color="text.disabled"
                              >
                                {repo.url}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Application */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Box display="flex" alignItems="center" gap={0.8}>
                            <Chip
                              label={typeInfo.label}
                              size="small"
                              sx={{
                                bgcolor: typeInfo.bg,
                                color: typeInfo.color,
                                fontWeight: 700,
                                fontSize: "9px",
                                height: 18,
                                borderRadius: "4px",
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontSize="11.5px"
                              fontWeight={500}
                              color="text.secondary"
                            >
                              {repo.appName}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Branch */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Chip
                            label={repo.branch}
                            size="small"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "10.5px",
                              height: 22,
                              fontWeight: 500,
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.06)"
                                  : "#f3f4f6",
                              color: "text.secondary",
                            }}
                          />
                        </TableCell>

                        {/* Description */}
                        <TableCell sx={{ py: 1.8, maxWidth: 220 }}>
                          <Typography
                            variant="body2"
                            fontSize="11.5px"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {repo.description}
                          </Typography>
                        </TableCell>

                        {/* Current Release */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Chip
                            label={repo.currentRelease || "—"}
                            size="small"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "10.5px",
                              height: 22,
                              fontWeight: 500,
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.06)"
                                  : "#f3f4f6",
                              color: "text.secondary",
                            }}
                          />
                        </TableCell>

                        {/* Incoming Release */}
                        <TableCell sx={{ py: 1.8 }}>
                          {repo.incomingRelease ? (
                            <Chip
                              label={repo.incomingRelease}
                              size="small"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "10.5px",
                                height: 22,
                                fontWeight: 700,
                                bgcolor: (theme) =>
                                  `${theme.palette.primary.main}15`,
                                color: "primary.main",
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </TabContext>

      {/* ══════════════════════════════════════════════ */}
      {/* ADD / EDIT APPLICATION DRAWER                  */}
      {/* ══════════════════════════════════════════════ */}
      <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer}>
        <Box
          sx={{
            width: "90vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : "#f8f9fa",
          }}
        >
          {/* Drawer Header with Save/Cancel */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) => theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  fontSize="15px"
                >
                  {editingIndex !== null
                    ? t("Edit Application")
                    : t("Add Application")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="11.5px"
                >
                  {editingIndex !== null
                    ? t("Update application details, type, and repositories")
                    : t("Add a new application to this project")}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize="11px"
                sx={{ mr: 1 }}
              >
                {editingApp.repos.length} repo
                {editingApp.repos.length !== 1 ? "s" : ""}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleCloseDrawer}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "7px",
                  minWidth: 80,
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveApplication}
                disabled={!editingApp.name.trim() || isCreating || isUpdating}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "7px",
                  minWidth: 80,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {editingIndex !== null
                  ? t("Save Changes")
                  : t("Add Application")}
              </Button>
            </Box>
          </Box>

          {/* Drawer Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.15)",
                borderRadius: "3px",
              },
            }}
          >
            {/* App Name */}
            <Box mb={2.5}>
              <TextField
                label={t("Application Name")}
                fullWidth
                size="medium"
                value={editingApp.name}
                required
                onChange={(e) =>
                  setEditingApp({ ...editingApp, name: e.target.value })
                }
                placeholder="e.g., Customer Portal"
              />
            </Box>

            {/* Application Type */}
            <Box mb={2.5}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                fontSize="11px"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1,
                  display: "block",
                }}
              >
                {t("Application Type")}{" "}
                <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <Box display="flex" gap={1.2}>
                {applicationTypeOptions.map((opt) => {
                  const isSelected = editingApp.type === opt.key;
                  return (
                    <Box
                      key={opt.key}
                      onClick={() =>
                        setEditingApp({
                          ...editingApp,
                          type: opt.key as Application["type"],
                        })
                      }
                      sx={{
                        flex: 1,
                        py: 1.5,
                        px: 1.5,
                        textAlign: "center",
                        borderRadius: "10px",
                        cursor: "pointer",
                        position: "relative",
                        border: (theme) =>
                          `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                        bgcolor: (theme) =>
                          isSelected
                            ? `${theme.palette.primary.main}0D`
                            : "transparent",
                        boxShadow: isSelected
                          ? "0 2px 8px rgba(99,102,241,0.15)"
                          : "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: (theme) => theme.palette.primary.main,
                          bgcolor: (theme) =>
                            isSelected
                              ? `${theme.palette.primary.main}14`
                              : `${theme.palette.action.hover}`,
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </Box>
                      )}
                      <Typography sx={{ fontSize: 22, mb: 0.5, lineHeight: 1 }}>
                        {opt.icon}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        fontWeight={700}
                        color={isSelected ? "primary.main" : "text.primary"}
                        sx={{ mb: 0.3 }}
                      >
                        {t(opt.label)}
                      </Typography>
                      <Typography
                        fontSize="10px"
                        fontWeight={400}
                        color="text.secondary"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {t(opt.desc)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Description */}
            <Box mb={2.5}>
              <TextField
                label={t("Description")}
                fullWidth
                multiline
                rows={3}
                value={editingApp.description}
                onChange={(e) =>
                  setEditingApp({
                    ...editingApp,
                    description: e.target.value,
                  })
                }
                placeholder="e.g., Customer-facing portal for account management, billing, support. Built with React/Next.js + Node.js API."
                helperText={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      mt: 0.3,
                      px: 1,
                      py: 0.3,
                      borderRadius: "4px",
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(139,92,246,0.1)"
                          : "linear-gradient(90deg, #ede9fe, #f3e8ff)",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "9px",
                        fontWeight: 800,
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
                        letterSpacing: 0.5,
                      }}
                    >
                      ✦ AI
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "10px",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "#c4b5fd" : "#6d28d9",
                        fontWeight: 500,
                      }}
                    >
                      {t(
                        "Better descriptions produce more accurate bug analysis, solutions & test scripts.",
                      )}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* SaaS Platform Binding */}
            {(editingApp.type === "saas" || editingApp.type === "hybrid") && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: "10px",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(16,185,129,0.08)"
                      : "#d1fae5",
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(16,185,129,0.2)" : "#6ee7b733"}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  fontSize="11px"
                  color="success.main"
                  sx={{ mb: 2, display: "block" }}
                >
                  ☁ {t("SaaS Platform Binding")}
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={platformOptions}
                    value={editingApp.platform || ""}
                    inputValue={platformInput}
                    onInputChange={(_, newValue) => setPlatformInput(newValue)}
                    onChange={async (_, newValue) => {
                      const val = (newValue as string) || "";
                      if (val && !platformOptions.includes(val) && id) {
                        setIsCreatingPlatform(true);
                        try {
                          await createTag({
                            project: id,
                            tagName: val,
                            type: "APPLICATION",
                          }).unwrap();
                          await refetchTags();
                        } catch (e) {
                          console.error("Failed to create platform tag:", e);
                        }
                        setIsCreatingPlatform(false);
                      }
                      setEditingApp({ ...editingApp, platform: val });
                    }}
                    filterOptions={(options, params) => {
                      const filtered = options.filter((o) =>
                        o
                          .toLowerCase()
                          .includes(params.inputValue.toLowerCase()),
                      );
                      if (
                        params.inputValue &&
                        !filtered.includes(params.inputValue)
                      ) {
                        filtered.push(params.inputValue);
                      }
                      return filtered;
                    }}
                    renderOption={(props, option) => {
                      const isNew = !platformOptions.includes(option);
                      return (
                        <li {...props}>
                          {isNew ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AddCircleOutlineOutlinedIcon
                                sx={{ fontSize: 16, color: "primary.main" }}
                              />
                              <Typography fontSize="13px">
                                {t("Create")} "{option}"
                              </Typography>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CheckIcon
                                sx={{
                                  fontSize: 16,
                                  visibility:
                                    option === editingApp.platform
                                      ? "visible"
                                      : "hidden",
                                }}
                              />
                              <Typography fontSize="13px">{option}</Typography>
                            </Box>
                          )}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Platform")}
                        placeholder="e.g., Salesforce"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isCreatingPlatform ? (
                                <CircularProgress size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    label={t("Current Release")}
                    fullWidth
                    required
                    value={editingApp.platformVersion || ""}
                    onChange={(e) =>
                      setEditingApp({
                        ...editingApp,
                        platformVersion: e.target.value,
                      })
                    }
                    placeholder="e.g., Spring '25"
                  />
                  <TextField
                    label={t("Environment URL")}
                    fullWidth
                    required
                    value={editingApp.envUrl || ""}
                    onChange={(e) =>
                      setEditingApp({
                        ...editingApp,
                        envUrl: e.target.value,
                      })
                    }
                    placeholder="https://mycompany.my.salesforce.com"
                  />
                  <TextField
                    label={t("Org / Instance ID")}
                    fullWidth
                    required
                    value={editingApp.orgId || ""}
                    onChange={(e) =>
                      setEditingApp({
                        ...editingApp,
                        orgId: e.target.value,
                      })
                    }
                    placeholder="00D5g00000XYZ"
                  />
                </Box>
              </Paper>
            )}

            {/* ── Repositories Section — shown for custom & hybrid ── */}
            {(editingApp.type === "custom" || editingApp.type === "hybrid") && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "10px",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  bgcolor: (theme) => theme.palette.background.paper,
                  overflow: "hidden",
                }}
              >
                {/* Repo Header */}
                <Box
                  onClick={() => setExpandedRepos(!expandedRepos)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: (theme) => `${theme.palette.primary.main}04`,
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CodeOutlinedIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      fontSize="13px"
                    >
                      {t("Repositories")}
                    </Typography>
                    <Chip
                      label={`${editingApp.repos.length} ${editingApp.repos.length === 1 ? "repo" : "repos"}`}
                      size="small"
                      sx={{ fontSize: "10px", height: 20 }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        addRepo();
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 600,
                        borderRadius: "6px",
                        py: 0.3,
                      }}
                    >
                      {t("Add Repo")}
                    </Button>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 20,
                        color: "text.secondary",
                        transition: "transform 0.2s",
                        transform: expandedRepos
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  </Box>
                </Box>

                {/* Repo List */}
                <Collapse in={expandedRepos}>
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      borderTop: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {editingApp.repos.length === 0 ? (
                      <Box
                        onClick={addRepo}
                        sx={{
                          mt: 2,
                          border: (theme) =>
                            `2px dashed ${theme.palette.divider}`,
                          borderRadius: "8px",
                          p: 3,
                          textAlign: "center",
                          cursor: "pointer",
                          color: "text.secondary",
                          fontSize: "12px",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: (theme) =>
                              `${theme.palette.primary.main}05`,
                          },
                        }}
                      >
                        {t("Click to add the first repository")}
                      </Box>
                    ) : (
                      editingApp.repos.map((repo, repoIndex) => (
                        <Paper
                          key={repoIndex}
                          elevation={0}
                          sx={{
                            p: 2,
                            mt: 1.5,
                            borderRadius: "8px",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "#f9fafb",
                            border: (theme) =>
                              `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1.5}
                          >
                            <Chip
                              label={`Repo ${repoIndex + 1}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "10px", height: 22 }}
                            />
                            <Tooltip title={t("Remove repo")}>
                              <IconButton
                                size="small"
                                onClick={() => removeRepo(repoIndex)}
                                sx={{ color: "error.main" }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {!isGitConfigured && (
                            <Alert
                              severity="warning"
                              sx={{ mb: 1.5, fontSize: "12px" }}
                              action={
                                <Button
                                  color="inherit"
                                  size="small"
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "11px",
                                  }}
                                  onClick={() =>
                                    navigate(
                                      "/company-settings/settings/integration",
                                    )
                                  }
                                >
                                  {t("Configure")}
                                </Button>
                              }
                            >
                              {t(
                                "GitHub is not configured. Please configure Git integration to select repositories.",
                              )}
                            </Alert>
                          )}
                          <Box
                            display="grid"
                            gridTemplateColumns="1fr 1fr"
                            gap={1.5}
                            mb={1.5}
                          >
                            <Autocomplete
                              size="small"
                              fullWidth
                              options={
                                repoData?.data?.decryptedData?.data || []
                              }
                              getOptionLabel={(option: any) =>
                                typeof option === "string"
                                  ? option
                                  : option?.name || option?.repositoryName || ""
                              }
                              value={
                                (
                                  repoData?.data?.decryptedData?.data || []
                                ).find(
                                  (r: any) =>
                                    r?.clone_url === repo.url ||
                                    r?.name === repo.url ||
                                    r?.repositoryName === repo.url,
                                ) || null
                              }
                              loading={isRepoLoading}
                              disabled={!isGitConfigured}
                              onChange={(_e, newValue: any) => {
                                const repoName =
                                  newValue?.name ||
                                  newValue?.repositoryName ||
                                  "";
                                const cloneUrl = newValue?.clone_url || "";
                                updateRepo(repoIndex, {
                                  ...repo,
                                  url: cloneUrl || repoName,
                                  branch: "",
                                });
                                if (repoName) {
                                  fetchBranches(repoIndex, repoName);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t("Repository URL")}
                                  placeholder="Select or type repository"
                                  required
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {isRepoLoading ? (
                                          <CircularProgress size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                            <Autocomplete
                              size="small"
                              fullWidth
                              freeSolo
                              options={
                                branchDataMap[`${repoIndex}`]?.data?.map(
                                  (b: any) => b?.name,
                                ) || []
                              }
                              value={repo.branch}
                              loading={
                                branchDataMap[`${repoIndex}`]?.loading || false
                              }
                              disabled={!isGitConfigured || !repo.url}
                              onChange={(_e, newValue) => {
                                updateRepo(repoIndex, {
                                  ...repo,
                                  branch: (newValue as string) || "",
                                });
                              }}
                              onInputChange={(_e, newInputValue, reason) => {
                                if (reason === "input") {
                                  updateRepo(repoIndex, {
                                    ...repo,
                                    branch: newInputValue,
                                  });
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t("Default Branch")}
                                  placeholder="main"
                                  required
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {branchDataMap[`${repoIndex}`]
                                          ?.loading ? (
                                          <CircularProgress size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Box>

                          <TextField
                            label={t("Description")}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={repo.description}
                            onChange={(e) =>
                              updateRepo(repoIndex, {
                                ...repo,
                                description: e.target.value,
                              })
                            }
                            placeholder="e.g., Frontend React app — handles UI, auth flows"
                            helperText={
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 0.8,
                                  py: 0.2,
                                  borderRadius: "3px",
                                  background: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(139,92,246,0.08)"
                                      : "#f5f3ff",
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "8px",
                                    fontWeight: 800,
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "#a78bfa"
                                        : "#7c3aed",
                                  }}
                                >
                                  ✦ AI
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "9.5px",
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "#c4b5fd"
                                        : "#6d28d9",
                                    fontWeight: 500,
                                  }}
                                >
                                  {t("Helps AI understand repo contents")}
                                </Typography>
                              </Box>
                            }
                            sx={{ mb: 1.5 }}
                          />

                          <Box
                            display="grid"
                            gridTemplateColumns="1fr 1fr"
                            gap={1.5}
                          >
                            <TextField
                              label={t("Current Release Tag")}
                              size="small"
                              fullWidth
                              value={repo.currentRelease}
                              onChange={(e) =>
                                updateRepo(repoIndex, {
                                  ...repo,
                                  currentRelease: e.target.value,
                                })
                              }
                              placeholder="release/v2.1"
                            />
                            <TextField
                              label={t("Incoming Release Tag")}
                              size="small"
                              fullWidth
                              value={repo.incomingRelease}
                              onChange={(e) =>
                                updateRepo(repoIndex, {
                                  ...repo,
                                  incomingRelease: e.target.value,
                                })
                              }
                              placeholder="release/v2.2"
                            />
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                </Collapse>
              </Paper>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* ══════════════════════════════════════════════ */}
      {/* DELETE CONFIRMATION DIALOG                     */}
      {/* ══════════════════════════════════════════════ */}
      <Dialog
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({
            open: false,
            index: -1,
            appId: "",
            name: "",
            repoCount: 0,
          })
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmberIcon sx={{ color: "error.main", fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700} fontSize="15px">
              {t("Delete Application")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "13px", lineHeight: 1.6 }}>
            {t("Are you sure you want to delete")}{" "}
            <strong>"{deleteDialog.name}"</strong>?{" "}
            {deleteDialog.repoCount > 0 &&
              t(
                "This will also remove all {{count}} associated repositories.",
                {
                  count: deleteDialog.repoCount,
                },
              )}{" "}
            {t("This cannot be undone.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setDeleteDialog({
                open: false,
                index: -1,
                appId: "",
                name: "",
                repoCount: 0,
              })
            }
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "7px",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "7px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {isDeleting ? t("Deleting...") : t("Delete Application")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
